import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useElectron } from "../../../../hooks/useElectron";
import { useAutoUpdater } from "../../../../hooks/useAutoUpdater";
import { ElectronGate } from "../../../electron";
import Modal from "../../../modals/modal.component";

/**
 * Component to display app version info and manage auto-updates
 */
export function EditProfileAppInfo(): JSX.Element {
  const { t } = useTranslation();
  const { version, versionLoading } = useElectron();
  const {
    state,
    progress,
    availableVersion,
    error,
    isAvailable,
    checkForUpdates,
    installUpdate,
    cancelDownload,
  } = useAutoUpdater();

  const [showInstallModal, setShowInstallModal] = useState(false);

  const displayVersion = versionLoading
    ? t("common.loading")
    : version || t("common.not-available");

  const isActionDisabled = state === 'checking' || state === 'downloading' || state === 'installing';

  /** Icono y texto de estado según el estado actual */
  const getStatusInfo = (): { message: string; type: 'success' | 'error' | 'loading'; icon: string } | null => {
    switch (state) {
      case 'checking':
        return { message: t("form.updater.checking"), type: 'loading', icon: 'la-spinner la-spin' };
      case 'downloading':
        return {
          message: t("form.updater.progress", { version: availableVersion, progress }),
          type: 'loading',
          icon: 'la-cloud-download-alt',
        };
      case 'up-to-date':
        return { message: t("form.updater.up-to-date"), type: 'success', icon: 'la-check-circle' };
      case 'download-complete':
        return {
          message: t("form.updater.download-complete", { version: availableVersion }),
          type: 'success',
          icon: 'la-box-open',
        };
      case 'installing':
        return { message: t("form.updater.checking"), type: 'loading', icon: 'la-spinner la-spin' };
      case 'error':
        return { message: error || t("form.updater.failed"), type: 'error', icon: 'la-exclamation-triangle' };
      default:
        return null;
    }
  };

  const statusInfo = getStatusInfo();

  // Auto-dismiss transient statuses (up-to-date) after a few seconds
  const [dismissedStatus, setDismissedStatus] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const prevStateRef = useRef(state);

  useEffect(() => {
    // Reset dismissed when state changes
    if (prevStateRef.current !== state) {
      setDismissedStatus(false);
      setFadingOut(false);
      prevStateRef.current = state;
    }

    if (state === 'up-to-date') {
      const fadeTimer = setTimeout(() => setFadingOut(true), 3500);
      const dismissTimer = setTimeout(() => setDismissedStatus(true), 4000);
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(dismissTimer);
      };
    }
  }, [state]);

  const shouldShowStatus = statusInfo && !(dismissedStatus && state === 'up-to-date');

  const handleInstallConfirm = async () => {
    setShowInstallModal(false);
    await installUpdate();
  };

  return (
    <div className="EditProfileAppInfoComponent">
      {/* Version card */}
      <div className="AppInfoCard">
        <div className="AppInfoCardHeader">
          <i className="las la-info-circle" />
          <span>{t("edit.profile.app-info.version")}</span>
        </div>
        <div className="AppInfoCardVersion">
          {displayVersion}
        </div>
      </div>

      <ElectronGate>
        {isAvailable && (
          <div className="AppUpdater">
            {/* Status banner */}
            {shouldShowStatus && statusInfo && (
              <div className={`AppUpdaterStatus ${statusInfo.type}${fadingOut ? ' fade-out' : ''}`}>
                <i className={`las ${statusInfo.icon}`} />
                <span>{statusInfo.message}</span>
              </div>
            )}

            {/* Progress section */}
            {state === 'downloading' && (
              <div className="AppUpdaterProgress">
                <div className="ProgressBar">
                  <div
                    className="ProgressBarFill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="ProgressLabel">{progress}%</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="AppUpdaterActions">
              {state !== 'download-complete' && (
                <button
                  className="AppUpdaterBtn primary"
                  onClick={checkForUpdates}
                  disabled={isActionDisabled}
                >
                  <i className={`las ${isActionDisabled ? 'la-spinner la-spin' : 'la-sync-alt'}`} />
                  {state === 'checking'
                    ? t("form.updater.checking")
                    : t("form.updater.check.title")}
                </button>
              )}

              {state === 'download-complete' && (
                <button
                  className="AppUpdaterBtn install"
                  onClick={() => setShowInstallModal(true)}
                >
                  <i className="las la-download" />
                  {t("form.updater.install.title")}
                </button>
              )}

              {state === 'downloading' && (
                <button
                  className="AppUpdaterBtn cancel"
                  onClick={cancelDownload}
                >
                  <i className="las la-times" />
                  {t("form.updater.cancel")}
                </button>
              )}
            </div>
          </div>
        )}
      </ElectronGate>

      {/* Install confirmation modal */}
      <Modal
        isOpen={showInstallModal}
        type="warning"
        onClose={() => setShowInstallModal(false)}
        body={{
          title: t("form.updater.install.title"),
          content: t("form.updater.install.confirm", { version: availableVersion }),
        }}
        buttons={[
          {
            text: t("form.updater.cancel"),
            class: "cancel",
            onClick: () => setShowInstallModal(false),
          },
          {
            text: t("form.updater.install.button"),
            class: "confirm",
            onClick: handleInstallConfirm,
          },
        ]}
      />
    </div>
  );
}
